import { BackgroundTask } from "@api/progress-api/progress-api-types";
import {
  BaseGroupIdProps,
  BaseProjectIdProps,
  CompanyId,
  ProjectId,
  UserId,
} from "@custom-types/base-types";
import { GUID } from "@faro-lotv/foundation";
import { BackgroundTaskState } from "@faro-lotv/service-wires";
import type { AnyFile } from "@utils/file-ext";
import { FileFromZip } from "@utils/file-from-zip";
import { ZipFile } from "@utils/zip-file";

/** Response of a single file upload */
export type SingleFileUploadResponse = string;

/** Base type for a single response of a multi file upload */
interface BaseMultiFileUploadResponse {
  /** The id of the upload task */
  id: GUID;

  /** The name of the file */
  fileName: string;
}

/** Successful response of an item in a multi file upload */
export interface UploadedFile extends BaseMultiFileUploadResponse {
  /** The size of the file */
  fileSize: number;

  /** The type of the file */
  fileType: string;

  /** The downloadable url of the file */
  downloadUrl: string;

  /** Md5Hash of the file */
  md5: string;
}

/** Failed response of an item in a multi file upload */
export interface UploadFailedFile extends BaseMultiFileUploadResponse {
  /** The error message reasoning the fail */
  errorMessage: string;
}

/** Canceled response of an item in a multi file upload */
export type CanceledUploadFile = BaseMultiFileUploadResponse;

/** Possible responses of an item in a multi file upload */
export type MultiFileUploadResponse =
  | UploadedFile
  | UploadFailedFile
  | CanceledUploadFile;

/** Response of a multi file upload job */
export interface MultiUploadedFileResponse {
  /** List of successful file uploads */
  successful: UploadedFile[];

  /** List of failed file uploads */
  failed: UploadFailedFile[];

  /** List oif canceled file uploads */
  canceled: CanceledUploadFile[];

  /** upload total Size in MB, considering only successfulUploads */
  uploadSizeMB: number;

  /** Upload time in secs, considering only successfulUploads */
  uploadTimeSecs: number;

  /** Speed in MB/s, considering only successfulUploads */
  uploadSpeedMBps?: number;

  /** split by buckets so it's easier to chart the Upload speed distribution in Amplitude */
  uploadSpeedBucketMBps?: "0-2" | "2-5" | "5-10" | "10-20" | "20-50" | ">50";
}

/** Possible file upload responses */
export type UploadedFileResponse =
  | SingleFileUploadResponse
  | MultiUploadedFileResponse;

/**
 * Used to determine the usage of the file upload by upload manager
 */
export enum UploadElementType {
  /** Default usage to upload files without specifying a project */
  default = "default",

  /** Upload files to a project */
  project = "project",

  /** Upload files for a project annotation */
  annotation = "annotation",

  /** Upload files to a group */
  group = "group",
}

/** Metadata of an existing file that may by re-used by CoreFileUploader. */
export type ExistingFileToReuse<T = unknown> = Pick<
  UploadedFile,
  "fileName" | "fileSize" | "md5" | "downloadUrl"
> & {
  /** Additional data that can be used e.g. in the optional finalizer(). */
  data: T;
};

/** Map from hash(filename, size) to file attributes, to identify if it's the same file. */
export type ExistingFilesToReuse<T = unknown> = Record<
  string,
  ExistingFileToReuse<T>
>;

/** Base context for a file upload task */
interface BaseFileUploadTaskContext {
  /** Usage type of the file */
  uploadElementType: UploadElementType;

  /** ID of the company associated to the file upload */
  companyId?: CompanyId;

  /** ID of the project associated to the file upload */
  projectId?: ProjectId;

  /** ID of the user associated to the file upload */
  userId?: UserId;

  /** Metadata of existing files that may by re-used by CoreFileUploader. */
  existingFilesToReuse?: ExistingFilesToReuse;
}

/** Default context for a file upload task */
interface DefaultFileUploadTaskContext extends BaseFileUploadTaskContext {
  uploadElementType: UploadElementType.default;
}

/** Base context for a file upload task targeted to an specific project */
type BaseProjectFileUploadTaskContext = BaseFileUploadTaskContext &
  BaseProjectIdProps;

/** Context for a file upload task targeted to an specific project */
interface ProjectFileUploadTaskContext
  extends BaseProjectFileUploadTaskContext {
  uploadElementType: UploadElementType.project;
}

/** Context for a file upload task targeted to an specific group */
interface GroupFileUploadTaskContext
  extends BaseFileUploadTaskContext,
    BaseGroupIdProps {
  uploadElementType: UploadElementType.group;
}

/** Context for a file upload task targeted to an specific annotation of a project */
interface AnnotationFileUploadTaskContext
  extends BaseProjectFileUploadTaskContext {
  uploadElementType: UploadElementType.annotation;

  /** ID of the markup (annotation) iElement associated to the file upload */
  iElementId: GUID;
}

export type FileUploadTaskContext =
  | DefaultFileUploadTaskContext
  | ProjectFileUploadTaskContext
  | AnnotationFileUploadTaskContext
  | GroupFileUploadTaskContext;

/** A task to track the upload of a file */
export interface FileUploadTask
  extends Omit<BackgroundTask, "status" | "taskType" | "context" | "tags"> {
  /** Name of the file */
  fileName: string;

  /** Whether progress toast notifications should be hidden */
  isSilent?: boolean;

  /** Current status of a task */
  status: BackgroundTaskState;

  /** Current progress amount of a task [percent] */
  progress: number;

  /** Expected end of task to show in ms from epoch (so it can be compared to Date.now()) */
  expectedEnd?: number;

  /** Speed of the upload in MB/s */
  speedMBps?: number;

  /** The error message if there is in a task */
  errorMessage?: string;

  /** Additional information of the file upload task */
  context: FileUploadTaskContext;
}

/** Necessary properties to upload a file */
export interface UploadFileParams {
  /** The file to upload */
  file: AnyFile | ZipFile;

  /** Callback function triggered when file upload begins */
  onUploadStart(): void;

  /** Callback function for retrieving the upload progress */
  onUploadProgress(progressEvent: ProgressEvent | number): void;

  /** Callback function triggered when file upload ends */
  onUploadComplete(fileUrl: string, context: FileUploadTaskContext): void;

  /** Additional information of the file upload task */
  context: FileUploadTaskContext;

  /** True to log an upload error silently. False for toast + logging (default). */
  isSilentError?: boolean;
}

/**
 * Extended finalizer callback, with access to all required properties to populate CaptureTreeEntity.pointClouds.
 * See @faro-lotv/service-wires > FinalizerCallback.
 */
export type SdbFinalizerCallback = (
  uploadedFile: UploadedFile
) => Promise<void>;

/** Necessary properties to upload multiple file */
export interface UploadMultipleFilesParams
  extends Pick<UploadFileParams, "onUploadStart" | "onUploadProgress"> {
  /** List of files to upload */
  files: (AnyFile | ZipFile | FileFromZip)[];

  /**
   * Optional callback, triggered after successful upload of each file.
   * If an error in occurs in the finalizer, the upload task will be marked as failed.
   *
   * The finalizer is currently not part of the rate-limiting (max parallel files),
   * 1) for technical reasons (cannot pass a function to UploadWorker),
   * 2) because waiting for the finalizers could slow down the actual uploads.
   * If needed, implement your own rate-limiting, e.g. using the "p-limit" library.
   */
  finalizer?: SdbFinalizerCallback;

  /** Callback function triggered when all file uploads have finished, successful or not. */
  onUploadComplete(
    uploadedResponse: MultiUploadedFileResponse,
    context: FileUploadTaskContext
  ): void;

  /** Additional information of the file upload task */
  context: FileUploadTaskContext;
}

/** All the params to validate file and to add failed upload task */
export interface ValidateFile {
  /** The file to validate */
  file: File;

  /** List of allowed extensions */
  allowedExtensions: string[];

  /** The maximum file size to validate */
  maxFileSize: number;

  /** Additional information of the file upload task */
  context: FileUploadTaskContext;
}

/** All the params to add a failed task in store while validate file */
export interface InvalidUploadToStore {
  /** The name of the file */
  fileName: string;

  /** Validation error message */
  errorMessage: string;

  /** Additional information of the file upload task */
  context: FileUploadTaskContext;
}
